import { useEffect, useState } from 'react';
import {
  adviserBillingApi,
  type AdviserBillingCard,
  type CompanyActivationOption,
  type CompanyPurchase,
} from '../../advisers/api';
import { safeWebUrl } from '../../advisers/AdviserProfileView';

const money = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(
    amount / 100
  );
export default function CompanyActivationPanel({
  companyId,
  practiceId,
  cards,
  onChanged,
  onPendingComponents,
}: {
  companyId: string;
  practiceId?: string;
  cards: AdviserBillingCard[];
  onChanged: () => Promise<void>;
  onPendingComponents?: (components: string[]) => void;
}) {
  const [options, setOptions] = useState<CompanyActivationOption[]>([]);
  const [purchases, setPurchases] = useState<CompanyPurchase[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [retry, setRetry] = useState(0);
  const [cardId, setCardId] = useState('');
  const [consent, setConsent] = useState(false);
  const [selectedProCycle, setSelectedProCycle] = useState<'monthly' | 'yearly'>('monthly');
  const payerType = practiceId ? 'practice' : 'client';
  const refresh = async () => {
    const next = await adviserBillingApi.activationOptions(companyId);
    setOptions(next.options);
    setPurchases(next.purchases);
    onPendingComponents?.(next.purchases.map((purchase) => purchase.component));
  };
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    adviserBillingApi
      .activationOptions(companyId)
      .then((next) => {
        if (active) {
          setOptions(next.options);
          setPurchases(next.purchases);
          onPendingComponents?.(next.purchases.map((purchase) => purchase.component));
        }
      })
      .catch((err) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [companyId, retry]);
  const buy = async (
    component: 'planning' | 'pro',
    billingCycle: 'monthly' | 'yearly',
    selectedCard: string,
    quote?: string
  ) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const purchase = await adviserBillingApi.purchase(companyId, component, {
        payerType,
        ...(practiceId && { practiceId }),
        paymentMethodId: selectedCard,
        authoriseRecurring: true,
        billingCycle,
        ...(quote && { quote }),
      });
      await refresh();
      await onChanged();
      setConsent(false);
      setNotice(
        purchase.status === 'active'
          ? 'Subscription activated. Company access has been updated.'
          : purchase.status === 'cancelled'
            ? 'The previous payment attempt expired or was cancelled. Review the current product terms before starting again.'
            : 'Your purchase is saved. Complete the existing invoice below to activate access.'
      );
    } catch (err) {
      setConsent(false);
      setError(err instanceof Error ? err.message : 'Activation needs attention.');
      await refresh().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };
  if (loading)
    return (
      <p className="adviser-muted" role="status">
        Checking available company plans…
      </p>
    );
  const visibleOptions = options.filter(
    (option) => option.component === 'planning' || option.billingCycle === selectedProCycle
  );
  if (!options.length && !purchases.length && !error) return null;
  return (
    <section className="adviser-activation-panel">
      <h3>{options.length ? 'Activate a company product' : 'Complete an existing purchase'}</h3>
      <p className="adviser-muted">
        Payments apply only to this company. Existing personal Founder coverage is kept separate.
      </p>
      {error && (
        <div className="adviser-error" role="alert">
          {error}
          <button type="button" onClick={() => setRetry((value) => value + 1)}>
            Refresh billing options
          </button>
        </div>
      )}
      {notice && (
        <p className="adviser-success" role="status">
          {notice}
        </p>
      )}
      {purchases.map((purchase) => {
        const ownsPayer =
          purchase.payerType === payerType &&
          (payerType !== 'practice' || purchase.payerPracticeId === practiceId);
        const invoice = safeWebUrl(purchase.invoiceUrl || '');
        return (
          <article className="adviser-billing-component" key={purchase.id}>
            <h3>
              {purchase.component === 'planning' ? 'Company planning' : 'Pro execution'} ·{' '}
              {purchase.status}
            </h3>
            <p>
              {purchase.payerType === 'practice' ? 'Adviser-paid' : 'Client-paid'} purchase ·{' '}
              {purchase.quantity} {purchase.component === 'planning' ? 'company' : 'seats'}
            </p>
            {purchase.error && <p className="adviser-muted">{purchase.error}</p>}
            {ownsPayer ? (
              <div className="adviser-actions">
                {invoice && (
                  <a
                    className="adviser-button"
                    href={invoice}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Complete existing invoice
                  </a>
                )}
                <button
                  type="button"
                  className="adviser-button adviser-button-secondary"
                  disabled={busy}
                  onClick={() =>
                    void buy(purchase.component, purchase.billingCycle, purchase.paymentMethodId)
                  }
                >
                  {busy ? 'Checking…' : 'Resume existing purchase'}
                </button>
              </div>
            ) : (
              <p className="adviser-notice">
                The authorised{' '}
                {purchase.payerType === 'practice'
                  ? 'practice owner'
                  : 'client billing administrator'}{' '}
                must complete this saved purchase. A second subscription will not be created.
              </p>
            )}
          </article>
        );
      })}
      {options.length > 0 && (
        <form className="adviser-panel" onSubmit={(event) => event.preventDefault()}>
          <p className="adviser-muted">
            Amounts shown are before any applicable taxes or credits. The saved Stripe invoice
            confirms the total.
          </p>
          <label>
            {practiceId ? 'Authorised practice card' : 'Authorised company card'}
            <select
              required
              value={cardId}
              onChange={(event) => {
                setCardId(event.target.value);
                setConsent(false);
              }}
            >
              <option value="">Choose a card for this company</option>
              {cards.map((card) => (
                <option value={card.id} key={card.id}>
                  {card.brand.toUpperCase()} •••• {card.last4} ·{' '}
                  {String(card.expMonth).padStart(2, '0')}/{card.expYear}
                </option>
              ))}
            </select>
          </label>
          {options.some((option) => option.component === 'pro') && (
            <label>
              Pro billing cycle
              <select
                value={selectedProCycle}
                onChange={(event) => {
                  setSelectedProCycle(event.target.value as 'monthly' | 'yearly');
                  setConsent(false);
                }}
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </label>
          )}
          <label className="adviser-consent">
            <input
              required
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
            />
            <span>
              I authorise recurring company charges to the selected{' '}
              {practiceId ? 'practice' : 'company'} card at the disclosed product price and renewal
              terms.
            </span>
          </label>
          <div className="adviser-activation-options">
            {visibleOptions.map((option) => (
              <article
                className="adviser-billing-component"
                key={`${option.component}-${option.billingCycle}`}
              >
                <h3>{option.component === 'planning' ? 'Company planning' : 'Pro execution'}</h3>
                <p className="adviser-activation-price">
                  {money(option.firstInvoiceAmount, option.currency)}
                  <span>
                    /{option.interval} {option.launchApplied ? 'initial rate' : ''}
                  </span>
                </p>
                <p>
                  {option.quantity}{' '}
                  {option.component === 'planning'
                    ? 'company plan'
                    : 'billable client seats; practice members excluded'}
                </p>
                {option.launchApplied && (
                  <p className="adviser-muted">
                    {option.promotionDescription} Standard product price:{' '}
                    {money(option.unitAmount * option.quantity, option.currency)}/{option.interval}.
                  </p>
                )}
                {option.trialEndsAt && (
                  <p className="adviser-notice">
                    Existing trial continues until{' '}
                    {new Date(option.trialEndsAt).toLocaleDateString()}. Paid billing starts at that
                    boundary.
                  </p>
                )}
                {option.requiresPlanning && (
                  <p className="adviser-notice">
                    Activate planning first, then return to activate Pro.
                  </p>
                )}
                <button
                  className="adviser-button"
                  type="button"
                  disabled={busy || !cardId || !consent || option.requiresPlanning}
                  onClick={() =>
                    void buy(option.component, option.billingCycle, cardId, option.quote)
                  }
                >
                  {busy
                    ? 'Saving purchase…'
                    : `Activate ${option.component === 'planning' ? 'company planning' : 'Pro'}`}
                </button>
              </article>
            ))}
          </div>
        </form>
      )}
    </section>
  );
}
